/**
 * AGENT-CRUISE seed data — cruise domain collections for kairos-demo.
 *
 * Collections:
 *   cruise_sailings       — 2 sailings (Norwegian Prima 3N Bermuda, Norwegian Bliss 7N Alaska)
 *   cruise_recipes        — 10 recipes spanning breakfast/lunch/dinner
 *   cruise_consumption    — 5 days of consumption records for Prima sailing
 *   cruise_provisioning_plans — empty at seed; generated by CruiseProvisioningService
 *   marpol_records        — 3 records with different disposal scenarios
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const T = DEMO_TENANT_ID;
const now = new Date();
const daysFromNow = (d) => new Date(now.getTime() + d * 86400000).toISOString().slice(0, 10);
const daysAgo = (d) => new Date(now.getTime() - d * 86400000).toISOString().slice(0, 10);

const baseMeta = () => ({
  tenantId: T,
  createdAt: now.toISOString(),
  updatedAt: now.toISOString(),
  createdBy: 'system',
  updatedBy: 'system',
  isDeleted: false,
  softDeleted: false,
  version: 1,
  source: 'cruise_seed',
});

// ── Sailings ──────────────────────────────────────────────────────────────────

export const SEED_CRUISE_SAILINGS = [
  {
    id: 'sail-prima-001',
    sailingId: 'sail-prima-001',
    shipName: 'Norwegian Prima',
    voyageNumber: 'V2400N',
    departurePort: 'NYC',
    arrivalPort: 'NYC',
    departureDate: daysFromNow(14),
    durationNights: 3,
    occupancyRate: 0.92,
    passengerCount: 3100,
    crewCount: 1400,
    diningVenues: ['The Local Bar & Grill', 'Onda by Scarpetta', 'Food Republic', 'Surfside'],
    itinerary: [
      { portCode: 'NYC',  arrivalDate: null,              departureDate: daysFromNow(14), isSpecialArea: false },
      { portCode: 'BDA',  arrivalDate: daysFromNow(15),   departureDate: daysFromNow(16), isSpecialArea: false },
      { portCode: 'NYC',  arrivalDate: daysFromNow(17),   departureDate: null,            isSpecialArea: false },
    ],
    status: 'confirmed',
    ...baseMeta(),
  },
  {
    id: 'sail-bliss-001',
    sailingId: 'sail-bliss-001',
    shipName: 'Norwegian Bliss',
    voyageNumber: 'V2401A',
    departurePort: 'SEA',
    arrivalPort: 'SEA',
    departureDate: daysFromNow(21),
    durationNights: 7,
    occupancyRate: 0.85,
    passengerCount: 4000,
    crewCount: 1700,
    diningVenues: ['The Local Bar & Grill', 'Los Lobos', 'Q Texas Smokehouse', 'Cagney\'s Steakhouse'],
    itinerary: [
      { portCode: 'SEA',  arrivalDate: null,              departureDate: daysFromNow(21), isSpecialArea: false },
      { portCode: 'JNU',  arrivalDate: daysFromNow(23),   departureDate: daysFromNow(23), isSpecialArea: false },
      { portCode: 'SKG',  arrivalDate: daysFromNow(24),   departureDate: daysFromNow(24), isSpecialArea: false },
      { portCode: 'KTN',  arrivalDate: daysFromNow(25),   departureDate: daysFromNow(25), isSpecialArea: false },
      { portCode: 'VIC',  arrivalDate: daysFromNow(26),   departureDate: daysFromNow(26), isSpecialArea: false },
      { portCode: 'SEA',  arrivalDate: daysFromNow(28),   departureDate: null,            isSpecialArea: false },
    ],
    status: 'confirmed',
    ...baseMeta(),
  },
];

// ── Recipes ───────────────────────────────────────────────────────────────────

export const SEED_CRUISE_RECIPES = [
  {
    id: 'rec-001',
    recipeId: 'rec-001',
    name: 'Classic Eggs Benedict',
    category: 'breakfast',
    servings: 1,
    venue: 'The Local Bar & Grill',
    ingredients: [
      { itemSku: 'CRU-EGG-001',  quantityPerServing: 2,    unit: 'ea',  shrinkFactor: 1.05 },
      { itemSku: 'CRU-HAM-001',  quantityPerServing: 0.05, unit: 'kg',  shrinkFactor: 1.08 },
      { itemSku: 'CRU-MUF-001',  quantityPerServing: 1,    unit: 'ea',  shrinkFactor: 1.02 },
      { itemSku: 'CRU-BUT-001',  quantityPerServing: 0.03, unit: 'kg',  shrinkFactor: 1.05 },
      { itemSku: 'CRU-LEM-001',  quantityPerServing: 0.01, unit: 'kg',  shrinkFactor: 1.20 },
    ],
    tenantId: T, ...baseMeta(),
  },
  {
    id: 'rec-002',
    recipeId: 'rec-002',
    name: 'Overnight Oats with Berries',
    category: 'breakfast',
    servings: 1,
    venue: 'Surfside',
    ingredients: [
      { itemSku: 'CRU-OAT-001',  quantityPerServing: 0.08, unit: 'kg',  shrinkFactor: 1.02 },
      { itemSku: 'CRU-MLK-001',  quantityPerServing: 0.15, unit: 'L',   shrinkFactor: 1.03 },
      { itemSku: 'CRU-BRY-001',  quantityPerServing: 0.06, unit: 'kg',  shrinkFactor: 1.15 },
      { itemSku: 'CRU-HON-001',  quantityPerServing: 0.02, unit: 'kg',  shrinkFactor: 1.02 },
    ],
    tenantId: T, ...baseMeta(),
  },
  {
    id: 'rec-003',
    recipeId: 'rec-003',
    name: 'Caesar Salad',
    category: 'lunch',
    servings: 1,
    venue: 'The Local Bar & Grill',
    ingredients: [
      { itemSku: 'CRU-ROM-001',  quantityPerServing: 0.12, unit: 'kg',  shrinkFactor: 1.25 },
      { itemSku: 'CRU-CHK-001',  quantityPerServing: 0.10, unit: 'kg',  shrinkFactor: 1.12 },
      { itemSku: 'CRU-PRM-001',  quantityPerServing: 0.02, unit: 'kg',  shrinkFactor: 1.05 },
      { itemSku: 'CRU-CRT-001',  quantityPerServing: 0.03, unit: 'kg',  shrinkFactor: 1.05 },
      { itemSku: 'CRU-DRS-001',  quantityPerServing: 0.04, unit: 'kg',  shrinkFactor: 1.02 },
    ],
    tenantId: T, ...baseMeta(),
  },
  {
    id: 'rec-004',
    recipeId: 'rec-004',
    name: 'Lobster Roll',
    category: 'lunch',
    servings: 1,
    venue: 'Surfside',
    ingredients: [
      { itemSku: 'CRU-LOB-001',  quantityPerServing: 0.18, unit: 'kg',  shrinkFactor: 1.30 },
      { itemSku: 'CRU-BUN-001',  quantityPerServing: 1,    unit: 'ea',  shrinkFactor: 1.03 },
      { itemSku: 'CRU-MAY-001',  quantityPerServing: 0.03, unit: 'kg',  shrinkFactor: 1.02 },
      { itemSku: 'CRU-CEL-001',  quantityPerServing: 0.02, unit: 'kg',  shrinkFactor: 1.20 },
    ],
    tenantId: T, ...baseMeta(),
  },
  {
    id: 'rec-005',
    recipeId: 'rec-005',
    name: 'Fish Tacos',
    category: 'lunch',
    servings: 1,
    venue: 'Food Republic',
    ingredients: [
      { itemSku: 'CRU-COD-001',  quantityPerServing: 0.12, unit: 'kg',  shrinkFactor: 1.15 },
      { itemSku: 'CRU-TRT-001',  quantityPerServing: 2,    unit: 'ea',  shrinkFactor: 1.02 },
      { itemSku: 'CRU-CAB-001',  quantityPerServing: 0.05, unit: 'kg',  shrinkFactor: 1.20 },
      { itemSku: 'CRU-LIM-001',  quantityPerServing: 0.02, unit: 'kg',  shrinkFactor: 1.25 },
      { itemSku: 'CRU-SLS-001',  quantityPerServing: 0.04, unit: 'kg',  shrinkFactor: 1.05 },
    ],
    tenantId: T, ...baseMeta(),
  },
  {
    id: 'rec-006',
    recipeId: 'rec-006',
    name: 'Filet Mignon',
    category: 'dinner',
    servings: 1,
    venue: 'Onda by Scarpetta',
    ingredients: [
      { itemSku: 'CRU-BEF-001',  quantityPerServing: 0.25, unit: 'kg',  shrinkFactor: 1.25 },
      { itemSku: 'CRU-POT-001',  quantityPerServing: 0.15, unit: 'kg',  shrinkFactor: 1.10 },
      { itemSku: 'CRU-ASP-001',  quantityPerServing: 0.08, unit: 'kg',  shrinkFactor: 1.15 },
      { itemSku: 'CRU-BUT-001',  quantityPerServing: 0.04, unit: 'kg',  shrinkFactor: 1.05 },
      { itemSku: 'CRU-RWNE-001', quantityPerServing: 0.15, unit: 'L',   shrinkFactor: 1.03 },
    ],
    tenantId: T, ...baseMeta(),
  },
  {
    id: 'rec-007',
    recipeId: 'rec-007',
    name: 'Pan-Seared Salmon',
    category: 'dinner',
    servings: 1,
    venue: 'The Local Bar & Grill',
    ingredients: [
      { itemSku: 'CRU-SAL-001',  quantityPerServing: 0.20, unit: 'kg',  shrinkFactor: 1.12 },
      { itemSku: 'CRU-SPN-001',  quantityPerServing: 0.10, unit: 'kg',  shrinkFactor: 1.20 },
      { itemSku: 'CRU-LEM-001',  quantityPerServing: 0.02, unit: 'kg',  shrinkFactor: 1.20 },
      { itemSku: 'CRU-CAPRS-001', quantityPerServing: 0.01, unit: 'kg',  shrinkFactor: 1.05 },
      { itemSku: 'CRU-EVOO-001', quantityPerServing: 0.02, unit: 'L',   shrinkFactor: 1.02 },
    ],
    tenantId: T, ...baseMeta(),
  },
  {
    id: 'rec-008',
    recipeId: 'rec-008',
    name: 'Chicken Marsala',
    category: 'dinner',
    servings: 1,
    venue: 'The Local Bar & Grill',
    ingredients: [
      { itemSku: 'CRU-CHK-001',  quantityPerServing: 0.22, unit: 'kg',  shrinkFactor: 1.12 },
      { itemSku: 'CRU-MSH-001',  quantityPerServing: 0.08, unit: 'kg',  shrinkFactor: 1.20 },
      { itemSku: 'CRU-MARS-001', quantityPerServing: 0.10, unit: 'L',   shrinkFactor: 1.03 },
      { itemSku: 'CRU-POT-001',  quantityPerServing: 0.15, unit: 'kg',  shrinkFactor: 1.10 },
      { itemSku: 'CRU-CREM-001', quantityPerServing: 0.05, unit: 'L',   shrinkFactor: 1.03 },
    ],
    tenantId: T, ...baseMeta(),
  },
  {
    id: 'rec-009',
    recipeId: 'rec-009',
    name: 'Shrimp Scampi',
    category: 'dinner',
    servings: 1,
    venue: 'Onda by Scarpetta',
    ingredients: [
      { itemSku: 'CRU-SHR-001',  quantityPerServing: 0.18, unit: 'kg',  shrinkFactor: 1.20 },
      { itemSku: 'CRU-LNG-001',  quantityPerServing: 0.10, unit: 'kg',  shrinkFactor: 1.05 },
      { itemSku: 'CRU-GAR-001',  quantityPerServing: 0.01, unit: 'kg',  shrinkFactor: 1.10 },
      { itemSku: 'CRU-WWNE-001', quantityPerServing: 0.10, unit: 'L',   shrinkFactor: 1.03 },
      { itemSku: 'CRU-BUT-001',  quantityPerServing: 0.04, unit: 'kg',  shrinkFactor: 1.05 },
    ],
    tenantId: T, ...baseMeta(),
  },
  {
    id: 'rec-010',
    recipeId: 'rec-010',
    name: 'Tiramisu',
    category: 'dinner',
    servings: 1,
    venue: 'Onda by Scarpetta',
    ingredients: [
      { itemSku: 'CRU-MASC-001', quantityPerServing: 0.08, unit: 'kg',  shrinkFactor: 1.05 },
      { itemSku: 'CRU-LF-001',   quantityPerServing: 3,    unit: 'ea',  shrinkFactor: 1.03 },
      { itemSku: 'CRU-ESP-001',  quantityPerServing: 0.04, unit: 'L',   shrinkFactor: 1.05 },
      { itemSku: 'CRU-CCO-001',  quantityPerServing: 0.01, unit: 'kg',  shrinkFactor: 1.05 },
      { itemSku: 'CRU-EGG-001',  quantityPerServing: 2,    unit: 'ea',  shrinkFactor: 1.05 },
    ],
    tenantId: T, ...baseMeta(),
  },
];

// ── Consumption records (5 days, Prima sailing) ───────────────────────────────

function makeConsumptionRecord(id, day, itemSku, qty, venue, source = 'pos') {
  return {
    id,
    recordId: id,
    sailingId: 'sail-prima-001',
    date: daysAgo(5 - day),
    itemSku,
    quantityConsumed: qty,
    venue,
    source,
    tenantId: T,
    ...baseMeta(),
  };
}

export const SEED_CRUISE_CONSUMPTION = [
  // Day 1
  makeConsumptionRecord('cons-001', 1, 'CRU-EGG-001', 620,  'The Local Bar & Grill'),
  makeConsumptionRecord('cons-002', 1, 'CRU-SAL-001', 58.5, 'The Local Bar & Grill'),
  makeConsumptionRecord('cons-003', 1, 'CRU-BEF-001', 72.1, 'Onda by Scarpetta'),
  // Day 2
  makeConsumptionRecord('cons-004', 2, 'CRU-EGG-001', 604,  'The Local Bar & Grill'),
  makeConsumptionRecord('cons-005', 2, 'CRU-LOB-001', 49.2, 'Surfside'),
  makeConsumptionRecord('cons-006', 2, 'CRU-SHR-001', 55.8, 'Onda by Scarpetta'),
  // Day 3
  makeConsumptionRecord('cons-007', 3, 'CRU-CHK-001', 82.3, 'The Local Bar & Grill'),
  makeConsumptionRecord('cons-008', 3, 'CRU-COD-001', 37.4, 'Food Republic'),
  makeConsumptionRecord('cons-009', 3, 'CRU-OAT-001', 23.1, 'Surfside'),
  // Day 4
  makeConsumptionRecord('cons-010', 4, 'CRU-BEF-001', 68.9, 'Onda by Scarpetta'),
  makeConsumptionRecord('cons-011', 4, 'CRU-SAL-001', 61.0, 'The Local Bar & Grill'),
  makeConsumptionRecord('cons-012', 4, 'CRU-ROM-001', 34.8, 'The Local Bar & Grill'),
  // Day 5
  makeConsumptionRecord('cons-013', 5, 'CRU-CHK-001', 79.4, 'The Local Bar & Grill'),
  makeConsumptionRecord('cons-014', 5, 'CRU-EGG-001', 592,  'The Local Bar & Grill'),
  makeConsumptionRecord('cons-015', 5, 'CRU-LOB-001', 52.1, 'Surfside'),
];

// ── MARPOL records (3 records — different disposal scenarios) ─────────────────

export const SEED_MARPOL_RECORDS = [
  {
    id: 'marpol-001',
    recordId: 'marpol-001',
    sailingId: 'sail-prima-001',
    date: daysAgo(3),
    wasteType: 'food_waste',
    quantityKg: 185.4,
    disposalMethod: 'overboard_discharge',
    distanceFromShore: 18.5,
    isSpecialArea: false,
    comminutedBelow25mm: true,
    plasticContent: false,
    location: { lat: 39.8, lon: -72.1, description: 'Open Atlantic, 18.5nm from shore' },
    tenantId: T, ...baseMeta(),
  },
  {
    id: 'marpol-002',
    recordId: 'marpol-002',
    sailingId: 'sail-prima-001',
    date: daysAgo(2),
    wasteType: 'food_waste',
    quantityKg: 92.8,
    disposalMethod: 'port_reception',
    distanceFromShore: 0.8,
    isSpecialArea: false,
    comminutedBelow25mm: true,
    plasticContent: false,
    location: { lat: 32.3, lon: -64.7, description: 'Bermuda (Kings Wharf) — port reception' },
    tenantId: T, ...baseMeta(),
  },
  {
    id: 'marpol-003',
    recordId: 'marpol-003',
    sailingId: 'sail-bliss-001',
    date: daysAgo(1),
    wasteType: 'food_waste',
    quantityKg: 301.2,
    disposalMethod: 'retain_on_board',
    distanceFromShore: 5.2,
    isSpecialArea: false,
    comminutedBelow25mm: false,
    plasticContent: false,
    location: { lat: 58.3, lon: -134.4, description: 'Inside Passage — retained pending Juneau port reception' },
    tenantId: T, ...baseMeta(),
  },
];

// ── Provisioning plans — empty at seed, populated by CruiseProvisioningService
export const SEED_CRUISE_PROVISIONING_PLANS = [];
